import { InvalidUrlError } from '../../../common/errors/errors';
import { DEFAULT_KEY_CERTS_PAGE_SIZE } from '../../../common/keys/components/KeyCertificatesTable';
import { DEFAULT_KEY_OPERATIONS_PAGE_SIZE } from '../../../common/keys/components/KeyOperationsTable';
import { KeyCertificateMapper } from '../../../common/keys/mappers/KeyCertificateMapper';
import { KeyOperationMapper } from '../../../common/keys/mappers/KeyOperationMapper';
import { KeyCertificate } from '../../../common/keys/models/KeyCertificate';
import { KeyOperation } from '../../../common/keys/models/KeyOperation';
import { KEY_CERTS_FRAGMENT } from '../../../common/keys/services/KeyCertificatesService';
import { KEY_OPERATIONS_FRAGMENT } from '../../../common/keys/services/KeyOperationsService';
import { DEFAULT_LOCATIONS_PAGE_SIZE } from '../../../common/locations/components/LocationsTable';
import { LocationMapper } from '../../../common/locations/mappers/LocationMapper';
import { Location } from '../../../common/locations/models/Location';
import { LOCATIONS_FRAGMENT } from '../../../common/locations/services/LocationsService';
import { BaseGQLService } from '../../../common/services/BaseGQLService';
import { FilterOperator } from '../../../common/types/gql-types';
import { CursorInfo, PaginationResult } from '../../../common/types/pagination';
import { ModelType } from '../../../common/utils/GQLIdEncoder';
import { ReportKeyDetailMapper } from '../mappers/ReportKeyDetailMapper';
import { ReportKeyMapper } from '../mappers/ReportKeyMapper';
import { ReportKey } from '../models/ReportKey';
import { ReportKeyDetail } from '../models/ReportKeyDetail';

export const GET_REPORT_KEY_QUERY = `
query ($keyId: ID!, $firstCertificates: Int, $firstLocations: Int, $firstOperations: Int) {
  node(id: $keyId) {
    ... on Key {
      id
      certificates(first: $firstCertificates) {
        ${KEY_CERTS_FRAGMENT}
      }
      locations(first: $firstLocations) {
        ${LOCATIONS_FRAGMENT}
      }
      operations(first: $firstOperations) {
        ${KEY_OPERATIONS_FRAGMENT}
      }
      operationSummary
      organizationKey {
        id
      }
      hasPrivateKey
      pkcs11Attributes
      shortDescription
      source
      valueJson
      metadata
      report {
        id
      }
    }
  }
}
`;

// TODO(a/1204586340740803): Support sorters
export const GET_REPORT_KEYS_QUERY = `
query (
  $reportId: ID!,
  $first: Int,
  $last: Int,
  $after: String,
  $before: String,
  $filters: KeysFilter
) {
  node(id: $reportId) {
    ... on ReportDone {
      keys(
        first: $first,
        last: $last,
        after: $after,
        before: $before,
        filters: $filters
      ) {
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
        edges {
          node {
            id
            shortDescription
            locations {
              totalCount
            }
            operationSummary
            information
            firstCallSite {
              class
              method
              file
              line
            }
          }
        }
        totalCount
      }
    }
  }
}
`;

export class ReportKeysRepository extends BaseGQLService {
  private readonly keyMapper = new ReportKeyMapper();
  private readonly keyDetailMapper = new ReportKeyDetailMapper();
  private readonly keyCertificateMapper = new KeyCertificateMapper();
  private readonly keyOperationMapper = new KeyOperationMapper();
  private readonly locationMapper = new LocationMapper();

  async getList(reportId: number, cursor: CursorInfo, filters: FilterOperator | null): Promise<PaginationResult<ReportKey>> {
    return this.query(
      GET_REPORT_KEYS_QUERY,
      {
        reportId: this.gqlIdEncoder.encode(ModelType.Report, reportId),
        ...this.mapCursorInfo(cursor),
        filters,
      },
      (data) => {
        if (!data.node) {
          throw new InvalidUrlError(`Invalid request: could not find Report ${reportId}`);
        }
        const { edges, pageInfo, totalCount } = data.node.keys;
        return {
          data: edges.map(({ node }: any) => this.keyMapper.mapGQLModel(node, reportId)),
          pageInfo,
          totalCount,
        };
      }
    );
  }

  async getKey(
    reportId: number,
    keyId: number
  ): Promise<{
    key: ReportKeyDetail;
    initialCertificates: PaginationResult<KeyCertificate>;
    initialOperations: PaginationResult<KeyOperation>;
    initialLocations: PaginationResult<Location>;
  }> {
    return this.query(
      GET_REPORT_KEY_QUERY,
      {
        keyId: this.gqlIdEncoder.encode(ModelType.Key, keyId),
        firstCertificates: DEFAULT_KEY_CERTS_PAGE_SIZE,
        firstOperations: DEFAULT_KEY_OPERATIONS_PAGE_SIZE,
        firstLocations: DEFAULT_LOCATIONS_PAGE_SIZE,
      },
      (data) => {
        if (!data.node) {
          throw new InvalidUrlError(`Invalid request: could not find Key ${keyId}`);
        }
        const { certificates, operations, locations, ...key } = data.node;
        const expectedReportId = this.gqlIdEncoder.encode(ModelType.Report, reportId);
        if (key.report.id !== expectedReportId) {
          throw new InvalidUrlError(`Invalid request: Key ${keyId} does not belong to Report ${reportId}`);
        }
        return {
          key: this.keyDetailMapper.mapGQLModel(key),
          initialCertificates: {
            pageInfo: certificates.pageInfo,
            totalCount: certificates.totalCount,
            data: certificates.edges.map((e: any) => this.keyCertificateMapper.mapGQLModel(e.node)),
          },
          initialOperations: {
            pageInfo: operations.pageInfo,
            totalCount: operations.totalCount,
            data: operations.edges.map((e: any) => this.keyOperationMapper.mapGQLModel(e.node)),
          },
          initialLocations: {
            pageInfo: locations.pageInfo,
            totalCount: locations.totalCount,
            data: locations.edges.map((e: any) => this.locationMapper.mapGQLModel(e.node)),
          },
        };
      }
    );
  }
}
